import React, { useEffect, useState, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { NotificationService } from '../../services/notificationService';
import { Notification } from '../../types';
import { useNavigate } from 'react-router-dom';
import { CollaborationService } from '../../services/collaborationService';
import Swal from 'sweetalert2';

export const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { user } = useStore();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;

    
    // Subscribe to notifications
    const unsubscribe = NotificationService.subscribeToNotifications(
      user.uid,
      (newNotifications) => {
       
        setNotifications(newNotifications);
      }
    );

    return () => {

      unsubscribe();
    };
  }, [user]);

  const handleNotificationClick = async (notification: Notification) => {
    try {
      
      if (notification.type === 'project_invitation') {
        await NotificationService.markAsRead(notification.id);
        const result = await Swal.fire({
          title: 'Project Invitation',
          text: notification.message,
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Accept',
          cancelButtonText: 'Decline',
          showCloseButton: true,
        });

        if (result.isConfirmed) {
          // Accept invitation
          if (!user?.uid || !user?.email) {
            throw new Error('User information is required to accept invitation');
          }
          await CollaborationService.acceptInvitation(
            notification.data.invitationId as string,
            user.uid,
            user.email
          );
          navigate(`/projects/${notification.data.projectId as string}`);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
            // Decline invitation
            await CollaborationService.rejectInvitation(notification.data.invitationId as string);
        }

        await NotificationService.markAsArchived(notification.id);
      } else if (notification.type === 'upcoming_deadline') {
        // Handle deadline notification click
        navigate('/deadlines');  // Redirect to deadlines page
        setIsOpen(false);  // Close the dropdown after clicking
      }
    } catch (error) {
      console.error('Error handling notification:', error);
      Swal.fire({
        title: 'Error',
        text: error instanceof Error ? error.message : 'Failed to process notification',
        icon: 'error',
      });
    }
  };

  const handleToggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggleDropdown}
        className="relative p-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 focus:outline-none"
      >
        <Bell className="h-6 w-6" />
        {notifications.length > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {notifications.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg overflow-hidden z-50">
          <div className="py-2">
            <div className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
              Notifications
          </div>
            {notifications.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                No new notifications
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                >
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {notification.title}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {notification.message}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {notification.createdAt instanceof Date
                      ? notification.createdAt.toLocaleString()
                      : new Date().toLocaleString()}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
